import React from 'react';
import {
  WorkspaceFilterBarSkeleton,
  WorkspaceTableSkeleton,
  WorkspaceTabsSkeleton,
  type WorkspaceTableSkeletonColumn,
} from '@/components/reusable/WorkspaceSkeleton';

/**
 * The single loading state for the PetPooja POS page (`/home/petpooja`).
 *
 * Opening it used to walk through a bare route spinner (the `<Suspense>`
 * fallback), the `FeatureRoute` "Loading..." spinner, blank `RoleRoute` /
 * `IntegrationRoute` frames, a grey `Loader2` while the connection loaded, and
 * — while workspaces were still loading — the "Connect your PetPooja account"
 * empty state. All of those now render this, which draws the dashboard's tab
 * strip, the menu-items toolbar and the items table, so the layout appears once
 * and only the rows change when data lands.
 */

// Menu items table: Item, Type, Category, Variations, Price, Stock,
// Availability, Catalog Sync.
const ITEMS_TABLE_COLUMNS: WorkspaceTableSkeletonColumn[] = [
  { width: 'w-[70%]' },
  { width: 'w-6' },
  { width: 'w-20', cellClassName: 'hidden sm:table-cell' },
  { width: 'w-12', cellClassName: 'hidden md:table-cell' },
  { width: 'w-14' },
  { width: 'w-10', cellClassName: 'hidden md:table-cell' },
  { width: 'w-16', cellClassName: 'hidden lg:table-cell' },
  { width: 'w-20', cellClassName: 'hidden lg:table-cell' },
];

/** Tabs: Menu Items, Orders, Addon Groups, Discounts. */
export const PetpoojaTabsSkeleton: React.FC = () => <WorkspaceTabsSkeleton widths={[76, 48, 92, 64]} />;

/** Items toolbar: search, Restaurant / Category / more filters, then Sync. */
export const PetpoojaToolbarSkeleton: React.FC = () => (
  <WorkspaceFilterBarSkeleton filters={3} actions={[112]} />
);

export const PetpoojaPageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-w-0 flex-col overflow-hidden bg-white"
    aria-busy="true"
    aria-label="Loading PetPooja"
    data-testid="petpooja-page-skeleton"
  >
    <PetpoojaTabsSkeleton />
    <div className="min-h-0 flex-1 overflow-hidden">
      <PetpoojaToolbarSkeleton />
      <div className="px-[15px] pb-8">
        <WorkspaceTableSkeleton columns={ITEMS_TABLE_COLUMNS} rows={8} data-testid="petpooja-table-skeleton" />
      </div>
    </div>
  </div>
);

export default PetpoojaPageSkeleton;
